import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import type { UpdateOrganizationSettingsRequest, CreateHolidayRequest } from '../types/organizations';
import { toast } from './use-toast';
import { queryKeys } from '../lib/queryKeys';
import { getApiErrorMessage } from '../lib/apiErrors';

export const QUERY_KEYS = {
  ORGANIZATION_SETTINGS: (organizationId: string) => ['organization', organizationId, 'settings'],
  CURRENT_ORGANIZATION: () => ['organization', 'current'],
  HOLIDAYS: (organizationId: string) => ['organization', organizationId, 'holidays'],
} as const;

export function useOrganizationSettings(organizationId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: QUERY_KEYS.ORGANIZATION_SETTINGS(organizationId),
    queryFn: async () => {
      const response = await apiService.getOrganizationSettings(organizationId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch organization settings'));
      }
      return response.data;
    },
    enabled: !!organizationId && (options?.enabled !== false),
  });
}

export function useCurrentOrganization(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: QUERY_KEYS.CURRENT_ORGANIZATION(),
    queryFn: async () => {
      const response = await apiService.getCurrentOrganization();
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch current organization'));
      }
      return response.data;
    },
    enabled: options?.enabled !== false,
  });
}

export function useUpdateOrganizationSettings(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settings: UpdateOrganizationSettingsRequest) => {
      const response = await apiService.updateOrganizationSettings(organizationId, settings);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to update organization settings'));
      }
      return response.data;
    },
    onSuccess: (data) => {
      // Update the settings cache
      queryClient.setQueryData(
        QUERY_KEYS.ORGANIZATION_SETTINGS(organizationId),
        data
      );

      // Also invalidate the current organization cache in case it includes settings
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.CURRENT_ORGANIZATION(),
      });

      // Invalidate admin organizations list (for org settings page logo display)
      queryClient.invalidateQueries({
        queryKey: queryKeys.profile.adminOrganizations(),
      });

      toast({
        title: 'Success',
        description: 'Organization settings updated successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update organization settings.',
        variant: 'destructive',
      });
    },
  });
}

// Business Holiday Hooks
export function useHolidays(organizationId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: QUERY_KEYS.HOLIDAYS(organizationId),
    queryFn: async () => {
      const response = await apiService.getHolidays(organizationId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch holidays'));
      }
      return response.data;
    },
    enabled: !!organizationId && (options?.enabled !== false),
  });
}

export function useAddHoliday(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateHolidayRequest) => {
      const response = await apiService.addHoliday(organizationId, request);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to add holiday'));
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.HOLIDAYS(organizationId),
      });

      toast({
        title: 'Success',
        description: 'Holiday added successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add holiday.',
        variant: 'destructive',
      });
    },
  });
}

export function useRemoveHoliday(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (holidayId: string) => {
      const response = await apiService.removeHoliday(organizationId, holidayId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to remove holiday'));
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.HOLIDAYS(organizationId),
      });

      toast({
        title: 'Success',
        description: 'Holiday removed successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove holiday.',
        variant: 'destructive',
      });
    },
  });
}